import { Button } from "primereact/button";
import React, { useContext, useEffect, useRef, useState } from "react";

import {
  Drawer,
  PanelWithDrawers,
  TopPanel,
} from "sj-ui-toolkit/src/atlas/components/PanelWithDrawers";
import { useUserContext } from "sj-ui-toolkit/src/atlas/contexts/User/UserContextProvider";
import { getExperimentClient } from "sj-ui-toolkit/src/client";
import { getMonacoLanguage } from "sj-ui-toolkit/src/helpers";

import {
  EXPERIMENT_CODE_SUBMISSION_CHANNEL,
  TOURNAMENT_EXPERIMENT_CODE_SUBMISSION_CHANNEL,
  initCable,
} from "sj-ui-toolkit/src/client/actionCable";

import { useLearnContext } from "../../../../../context/LearnContext";
import { useTournamentContext } from "../../../../../context/TournamentContext";
import { VideoContext } from "../../VideoContext";

import ExerciseLogs from "./ExerciseLogs";

import { useMonacoThemes } from "../../../../../hooks/useMonacoThemes";
import EditorSkeleton from "../../../SandboxLesson/RightPanel/Tabs/Editor/EditorSkeleton";

const MonacoLocal = React.lazy(
  () => import("../../../SandboxLesson/RightPanel/Tabs/Editor/MonacoLocal")
);

import S from "./_styles.module.scss";

export default function Exercise({ exercise, variant }) {
  const {
    isLessonComplete,
    onQuizTake,
    onLessonFinish,
    isExercisePassed,
    onExercisePassed,
    tournamentUuid,
    exerciseOnly,
  } = useContext(VideoContext);
  const { selectedTenant } = useUserContext();
  const { isTournament } = useLearnContext();
  const { handleAttempt } = useTournamentContext();
  const eventContext = {
    tenant: selectedTenant,
    tournamentUuid,
  };

  const runIdRef = useRef(null);
  const [logs, setLogs] = useState("");
  const [isLogsOpen, setIsLogsOpen] = useState(false);
  const [isTestsRunning, setIsTestRunning] = useState(false);

  const { editorRef, monacoTheme, handleEditorMount } = useMonacoThemes();

  const handleRun = async () => {
    setIsLogsOpen(true);
    setIsTestRunning(true);
    const experimentClient = getExperimentClient();
    const { lessonKey, lessonVersion, filePath, currentVariant } = variant;

    const { runId } = await experimentClient.runTests(
      lessonKey,
      lessonVersion,
      filePath,
      editorRef.current.getValue(),
      currentVariant,
      eventContext
    ).promise;

    runIdRef.current = runId;
  };

  const handleReset = () => {
    if (editorRef.current) {
      editorRef.current.setValue(variant.codeContent);
    }
    setLogs("");
  };

  const onReceive = async message => {
    if (exercise.attemptId !== message.attemptId) {
      setIsTestRunning(false);
      return;
    }

    if (message.status === "failed") {
      console.error(message.message);
      setIsTestRunning(false);
      return;
    }

    const experimentClient = getExperimentClient();
    const status = await experimentClient.checkStatus(runIdRef.current).promise;
    setLogs(status);
    setIsTestRunning(false);
    if (isTournament) {
      handleAttempt({
        maxPoints: message.points,
        attemptsCount: message.attemptsCount,
      });
    }
    if (status.status === "passed") {
      onExercisePassed();
    }
  };

  useEffect(() => {
    const channel = isTournament
      ? TOURNAMENT_EXPERIMENT_CODE_SUBMISSION_CHANNEL
      : EXPERIMENT_CODE_SUBMISSION_CHANNEL;
    const subscription = initCable(channel, message => onReceive(message), { camelizeKeys: true });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  return (
    <PanelWithDrawers resizable={true}>
      <TopPanel className="bg-elevation-1 rounded-l-sm">
        <div className="flex flex-col h-full">
          <div className="flex justify-between items-center p-3">
            <Button label="Run" icon="pi pi-check" onClick={handleRun} loading={isTestsRunning} />
            <Button label="Reset coding exercise" icon="pi pi-refresh" link onClick={handleReset} />
          </div>
          <div className="flex-1 min-h-0">
            <React.Suspense fallback={<EditorSkeleton />}>
              <MonacoLocal
                height="100%"
                theme={monacoTheme}
                className={S.editor}
                language={getMonacoLanguage(exercise.language)}
                defaultValue={variant.codeContent}
                onMount={handleEditorMount}
              />
            </React.Suspense>
          </div>
          {isLessonComplete && (
            <div className="flex justify-end items-center p-3">
              <Button
                label={`Finish ${exerciseOnly ? "exercise" : "lesson"}`}
                icon="pi pi-check"
                onClick={onLessonFinish}
              />
            </div>
          )}
          {!isLessonComplete && isExercisePassed && (
            <div className="flex justify-end items-center p-3">
              <Button label="Take the quiz" onClick={onQuizTake} />
            </div>
          )}
        </div>
      </TopPanel>
      <Drawer
        initialHeight="50%"
        title="Logs"
        isOpen={isLogsOpen}
        onOpenToggle={open => setIsLogsOpen(open)}
        className="bg-elevation-1 rounded-tl-sm pl-3 overflow-y-auto min-w-0 overflow-hidden"
      >
        <ExerciseLogs logs={logs} isTestsRunning={isTestsRunning} />
      </Drawer>
    </PanelWithDrawers>
  );
}
